import {
  Box,
  Separator,
  Flex,
  FlexProps,
  Field,
  Heading,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  Text,
  Textarea as ChakraTextarea,
  TextareaProps as ChakraTextareaProps,
  FieldHelperTextProps,
} from '@chakra-ui/react';
import { ComponentProps, forwardRef, ForwardedRef, Fragment, ReactElement } from 'react';
import { useLocation } from 'react-router';
import InfoIcon from 'src/assets/icons/InfoIcon';
import Dropdown from 'src/components/input/Dropdown';
import fonts from 'src/theme/fonts';
import colors from 'src/theme/colors';
import {
  Checkbox as ChakraCheckbox,
  CheckboxProps as ChakraCheckboxProps,
} from 'src/components/ui/checkbox';
import { Tooltip } from 'src/components/ui/tooltip';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { APPLE_PODCAST_CATEGORIES } from './const';

type FormContainerRef = HTMLDivElement;
type FormContainerProps = FlexProps & {
  heading?: string;
  subHeading?: string;
  id?: string;
  urlHash?: `#${string}` | '';
  hideDivider?: boolean;
};

export const FormContainer = forwardRef<FormContainerRef, FormContainerProps>(
  ({ heading, subHeading, id, urlHash, hideDivider, ...props }, ref) => {
    const { hash, pathname } = useLocation();

    // if the url hash is not the same as the current hash, don't render the component
    // but only on settings page as that's the only place with tabbed navigation that uses hash
    if (pathname.includes('settings') && hash && hash !== urlHash) {
      return null;
    }

    return (
      <Flex
        as="section"
        direction="column"
        p="33px 50px"
        bg={colors.white}
        boxShadow={`inset 0 0 0 2px ${colors.podcastShade200}`}
        borderRadius="12px"
        id={id}
        ref={ref}
        {...props}
      >
        {(heading || subHeading) && (
          <>
            <SectionHeader heading={heading} subHeading={subHeading} />
            {!hideDivider && <Separator mb="1.8125rem" />}
          </>
        )}
        {props.children}
      </Flex>
    );
  },
);

type InputRef = HTMLInputElement;
type InputProps = ChakraInputProps & {
  label: string;
  id: string;
  helperText?: string;
  errorMessage?: string;
  isOptional?: boolean;
  tooltipLabel?: string;
  invalid?: boolean;
};

function SectionHeader({ heading, subHeading }: { heading?: string; subHeading?: string }) {
  return (
    <Box m="0 0 18px">
      {heading && <Heading visual="sectionHeader">{heading}</Heading>}
      {subHeading && (
        <Text as="h6" w="max-content" p={0} color="podcastShade500">
          {subHeading}
        </Text>
      )}
    </Box>
  );
}
export const Input = forwardRef<InputRef, InputProps>(
  ({ label, id, helperText, errorMessage, isOptional, tooltipLabel, invalid, ...props }, ref) => (
    <Field.Root id={id} invalid={invalid}>
      <Flex alignItems="center">
        <Field.Label display="flex">
          {label}
          {isOptional && (
            <Text ml="5px" fontFamily={fonts.bold}>
              (optional)
            </Text>
          )}
        </Field.Label>
        {tooltipLabel && (
          <Tooltip content={tooltipLabel}>
            <Box ml="7px">
              <InfoIcon mb="7px" />
            </Box>
          </Tooltip>
        )}
      </Flex>
      <ChakraInput type="text" ref={ref} {...props} />
      <Field.ErrorText>{errorMessage}</Field.ErrorText>
      {helperText && <Field.HelperText {...fieldHelperTextProps}>{helperText}</Field.HelperText>}
    </Field.Root>
  ),
);

export function InputGroup({
  label,
  id,
  helperText,
  errorMessage,
  isOptional,
  tooltipLabel,
  invalid,
  children,
}: InputProps & {
  children: React.ReactNode;
}) {
  return (
    <Field.Root id={id} invalid={invalid}>
      <Flex alignItems="center">
        <Field.Label display="flex">
          {label}
          {isOptional && (
            <Text ml="5px" fontFamily={fonts.bold}>
              (optional)
            </Text>
          )}
        </Field.Label>
        {tooltipLabel && (
          <Tooltip content={tooltipLabel}>
            <Box ml="7px">
              <InfoIcon mb="7px" />
            </Box>
          </Tooltip>
        )}
      </Flex>
      {children}
      <Field.ErrorText>{errorMessage}</Field.ErrorText>
      {helperText && <Field.HelperText {...fieldHelperTextProps}>{helperText}</Field.HelperText>}
    </Field.Root>
  );
}

type TextareaRef = HTMLTextAreaElement;
type TextareaProps = ChakraTextareaProps & {
  label: string;
  id: string;
  helperText?: string;
  errorMessage?: string;
  invalid?: boolean;
};
export const Textarea = forwardRef<TextareaRef, TextareaProps>(
  ({ id, label, helperText, errorMessage, ...props }, ref) => (
    <Field.Root id={id} invalid={props.invalid}>
      <Field.Label>{label}</Field.Label>
      <ChakraTextarea ref={ref} {...props} />
      <Field.ErrorText>{errorMessage}</Field.ErrorText>
      {helperText && <Field.HelperText {...fieldHelperTextProps}>{helperText}</Field.HelperText>}
    </Field.Root>
  ),
);

type DropdownRef = HTMLSelectElement;
type PodcastCategoryDropdownProps = Omit<ComponentProps<typeof Dropdown>, 'children'>;
export const PodcastCategoryDropdown = forwardRef<DropdownRef, PodcastCategoryDropdownProps>(
  (props, ref) => (
    <Dropdown {...props} ref={ref} helperProps={fieldHelperTextProps}>
      {APPLE_PODCAST_CATEGORIES.map(category => (
        <Fragment key={category}>
          <option value={category}>{category}</option>
        </Fragment>
      ))}
    </Dropdown>
  ),
);

type CheckboxRef = HTMLInputElement;
type CheckboxProps<T extends FieldValues> = ChakraCheckboxProps & {
  label?: string;
  id: string;
  helperText?: string;
  isOptional?: boolean;
  tooltipText?: string;
  control: Control<T>;
};
function CheckboxComponent<T extends FieldValues>(
  { id, label, helperText, isOptional, tooltipText, control, ...props }: CheckboxProps<T>,
  ref: ForwardedRef<CheckboxRef>,
) {
  return (
    <Controller
      name={id as Path<T>}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Field.Root
          id={id}
          {...(tooltipText && {
            w: 'auto',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
          })}
        >
          <Field.Label display="flex">
            {label}
            {isOptional && (
              <Text ml="5px" fontFamily={fonts.bold}>
                (optional)
              </Text>
            )}
          </Field.Label>
          <ChakraCheckbox
            ref={ref}
            color="podcastShade500"
            fontFamily={fonts.normal}
            fontSize="1rem"
            lineHeight="1.125rem"
            onCheckedChange={e => onChange(e.checked === true)}
            checked={value === true}
            {...props}
          />
          {tooltipText && (
            <Tooltip content={tooltipText} openDelay={0}>
              <Box ml="10px">
                <InfoIcon mb="7px" />
              </Box>
            </Tooltip>
          )}
          {helperText && (
            <Field.HelperText {...fieldHelperTextProps}>{helperText}</Field.HelperText>
          )}
        </Field.Root>
      )}
    />
  );
}

export const Checkbox = forwardRef(CheckboxComponent) as <T extends FieldValues>(
  props: CheckboxProps<T> & { ref?: ForwardedRef<CheckboxRef> },
) => ReactElement;

export const fieldHelperTextProps: FieldHelperTextProps = {
  color: 'podcastShade400',
  bg: 'none',
  border: 'none',
  p: 0,
  mt: '5px',
  h: '2rem',
};
